import { z } from 'zod';
import { QuoteLocationSchema } from 'api/accessexpress/schema';
import { CreateChildAccountSchema } from 'api/accessexpressv3/schemas/AccessCheckoutSchemas';
import { VALID_PHONE_ERROR_LABEL } from 'shared/constants/error-labels';
import { VALID_PHONE_NUMBER } from 'shared/constants/validation-regex-constants';

const dateFormat = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-\d{4}$/;

const BandwidthSchema = z.object({
  amount: z
    .string()
    .nonempty('Required')
    .refine((val) => !isNaN(Number(val)), {
      message: 'Must be a number',
    }),
  unit: z.object({
    label: z.enum(['Mb/s', 'Gb/s']),
    value: z.enum(['Mbps', 'Gbps']),
  }),
});

const DateSchema = z
  .string()
  .regex(dateFormat, 'Must be in MM-DD-YYYY format')
  .transform((val) => {
    const [month, day, year] = val.split('-');
    return `${year}-${month}-${day}`; // normalized YYYY-MM-DD for BE
  });

const PhoneNumberSchema = z
  .string()
  .nonempty(VALID_PHONE_ERROR_LABEL)
  .length(10, VALID_PHONE_ERROR_LABEL)
  .regex(VALID_PHONE_NUMBER, VALID_PHONE_ERROR_LABEL);

export const RebillServiceItemSchema = z
  .object({
    service_type: z.object({
      label: z.string().nonempty('Required'),
      value: z.string().nonempty('Required'),
    }),
    service_identifier: z.string().optional(),
    subtype: z.string().optional(),
    bandwidth: BandwidthSchema.optional(),
    btn: PhoneNumberSchema.optional(),
    wtns: z.array(PhoneNumberSchema).max(5).optional(),
    description: z.string().optional(),
  })
  .superRefine((svc, ctx) => {
    const type = svc.service_type?.value;

    if (['Broadband', 'DIA', 'PIP/GSE'].includes(type)) {
      if (!svc.service_identifier) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_identifier'],
          message: 'Required',
        });
      }
      if (!svc.subtype) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['subtype'],
          message: 'Required',
        });
      }
      if (!svc.bandwidth) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bandwidth'],
          message: 'Required',
        });
      }
    }

    if (type === 'Mobility') {
      if (!svc.service_identifier) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_identifier'],
          message: 'Required',
        });
      }
      if (!svc.bandwidth) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bandwidth.amount'],
          message: 'Required',
        });

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['bandwidth.unit'],
          message: 'Required',
        });
      }
    }

    if (
      [
        'DID',
        'POTS',
        'RCF',
        'SIP',
        'Toll-Free',
        'LD',
        'Hosted PBX/EPOTS',
      ].includes(type)
    ) {
      if (!svc.btn) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['btn'],
          message: 'Required',
        });
      }

      if (svc.wtns) {
        svc.wtns.forEach((wtn, idx) => {
          if (!wtn) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['wtns', idx],
              message: `Required`,
            });
          }
        });
      }
    }

    if (type === 'Television') {
      if (!svc.service_identifier) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['service_identifier'],
          message: 'Required',
        });
      }
    }
  });

export const RebillServiceDetailsSchema = z
  .object({
    site: QuoteLocationSchema,
    business_name: z.string().nonempty('Required'),
    unit: z.string().optional(),
    name: z.string().nonempty('Required'),
    phone_number: z.string().nonempty('Required'),

    vendor_name: z.string().nonempty('Required'),
    vendor_account_number: z.string().nonempty('Required'),
    mrc: z
      .union([z.string(), z.number()])
      .refine(
        (val) => {
          if (typeof val === 'string') return !isNaN(parseFloat(val));
          return !isNaN(val);
        },
        { message: 'MRC must be a number' },
      )
      .transform((val) => (typeof val === 'string' ? parseFloat(val) : val)),
    contract_start_date: DateSchema.optional().or(z.literal('')),
    contract_end_date: DateSchema.optional().or(z.literal('')),

    services: z
      .array(RebillServiceItemSchema)
      .min(1, 'At least one service is required'),
  })
  .merge(CreateChildAccountSchema)
  .superRefine((data, ctx) => {
    if (!data.site?.macnum) {
      if (!data.parent_account) {
        ctx.addIssue({
          path: ['parent_account'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.location_name) {
        ctx.addIssue({
          path: ['location_name'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.billing_type) {
        ctx.addIssue({
          path: ['billing_type'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.invoice_delivery_method) {
        ctx.addIssue({
          path: ['invoice_delivery_method'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.invoice_style) {
        ctx.addIssue({
          path: ['invoice_style'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.location_contact_email) {
        ctx.addIssue({
          path: ['location_contact_email'],
          message: 'Required',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

export type RebillServiceDetails = z.infer<typeof RebillServiceDetailsSchema>;
export type RebillServiceItem = z.infer<typeof RebillServiceItemSchema>;
